import { useEffect, useLayoutEffect, useState } from "react"

import { cn } from "@productlane/lib/src/cn"

// The dot's 200ms exit, then the text's 200ms slide after a 200ms delay.
export const READ_ANIMATION_MS = 400

type Phase = "idle" | "leaving" | "enter-from" | "entering"

/** The dot's width plus its gap, so the slide ends on the pixel the dot frees. */
export type ReadSlideOffset = "-translate-x-3" | "-translate-x-3.5"

const EASE =
	"[transition-timing-function:cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none"

const DOT_CLASS: Record<Phase, string> = {
	idle: "",
	leaving: cn(
		"scale-0 opacity-0 transition-[opacity,transform] duration-200",
		EASE,
	),
	"enter-from": "scale-0 opacity-0 transition-none",
	entering: cn("transition-[opacity,transform] delay-200 duration-200", EASE),
}

function slideClass(phase: Phase, offset: ReadSlideOffset) {
	switch (phase) {
		case "leaving":
			return cn(offset, "transition-transform delay-200 duration-200", EASE)
		case "enter-from":
			return cn(offset, "transition-none")
		case "entering":
			return cn("transition-transform duration-200", EASE)
		case "idle":
			return ""
	}
}

/**
 * On read the dot fades and shrinks, then the text slides into its space; the dot
 * leaves the layout only once the slide ends, so the swap lands on the same pixel.
 * Marking unread plays it in reverse: the text slides back, then the dot grows in.
 */
export function useReadTransition(
	isUnread: boolean,
	offset: ReadSlideOffset,
	rowId?: string,
) {
	const [prev, setPrev] = useState({ isUnread, rowId })
	const [phase, setPhase] = useState<Phase>("idle")
	if (prev.isUnread !== isUnread || prev.rowId !== rowId) {
		setPrev({ isUnread, rowId })
		// Index-keyed virtual rows get a new thread on tab switch or reorder; snap those.
		if (prev.rowId !== rowId) setPhase("idle")
		else setPhase(isUnread ? "enter-from" : "leaving")
	}

	useLayoutEffect(() => {
		if (phase !== "enter-from") return
		// Commit the start pose before paint so the next class swap transitions from it.
		document.body.getBoundingClientRect()
		setPhase("entering")
	}, [phase])

	useEffect(() => {
		if (phase !== "leaving" && phase !== "entering") return
		const timer = setTimeout(() => setPhase("idle"), READ_ANIMATION_MS)
		return () => clearTimeout(timer)
	}, [phase])

	return {
		showDot: isUnread || phase === "leaving",
		dotClassName: DOT_CLASS[phase],
		slideClassName: slideClass(phase, offset),
	}
}
